import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { manageBookingApi, AvailabilityDay, AvailabilitySlot } from '../services/api';
import { useTheme } from '../hooks/useTheme';
import {
    Calendar,
    User,
    AlertTriangle,
    Check,
    ChevronRight,
    ChevronLeft,
    Sun,
    Moon,
    Globe,
    X,
    RefreshCw,
    ExternalLink,
    MessageSquare
} from 'lucide-react';
import { Footer } from '../components/Footer';
import { detectTimezone, TIMEZONES } from '../utils/timezones';

const DAYS_OF_WEEK = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

const formatDateReadable = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);

    const monthName = date.toLocaleDateString('en-US', { month: 'long' });
    const dayNum = date.getDate();
    const yearNum = date.getFullYear();

    const suffix = (d: number) => {
        if (d > 3 && d < 21) return 'th';
        switch (d % 10) {
            case 1: return 'st';
            case 2: return 'nd';
            case 3: return 'rd';
            default: return 'th';
        }
    };

    return `${monthName} ${dayNum}${suffix(dayNum)}, ${yearNum}`;
};

interface BookingDetails {
    id: string;
    requester_name: string;
    requester_email: string;
    status: string;
    confirmed_slot: string;
    meeting_time_formatted: string | null;
    teams_meeting_url?: string;
    cancelled_at?: string;
    cancelled_by?: string;
    cancel_reason?: string;
    created_at?: string;
}

interface LinkInfo {
    name: string;
    description?: string;
    duration_minutes: number;
    user_name: string;
    user_email: string;
    timezone: string;
    logo_url?: string;
    default_theme: 'dark' | 'light' | 'system';
}

interface Policy {
    allow_reschedule: boolean;
    allow_cancel: boolean;
    min_notice_hours: number;
    can_reschedule: { allowed: boolean; reason?: string };
    can_cancel: { allowed: boolean; reason?: string };
}

type View = 'details' | 'reschedule' | 'cancel' | 'success';

export function ManageBookingPage() {
    const { token } = useParams<{ token: string }>();

    const [booking, setBooking] = useState<BookingDetails | null>(null);
    const [link, setLink] = useState<LinkInfo | null>(null);
    const [policy, setPolicy] = useState<Policy | null>(null);
    const [availability, setAvailability] = useState<AvailabilityDay[]>([]);
    const [timezone, setTimezone] = useState(() => detectTimezone());
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const { toggleTheme, isDark } = useTheme({
        defaultTheme: link?.default_theme || 'dark',
        storageKey: `manage-${token}`
    });

    const [view, setView] = useState<View>('details');
    const [successMessage, setSuccessMessage] = useState<string>('');

    const [selectedDate, setSelectedDate] = useState<string | null>(null);
    const [selectedSlot, setSelectedSlot] = useState<AvailabilitySlot | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [cancelReason, setCancelReason] = useState('');
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);

    const initialMonth = useMemo(() => {
        if (availability.length > 0) {
            const firstAvailable = availability.find(d => d.slots.length > 0);
            if (firstAvailable) {
                const firstDate = new Date(firstAvailable.date + 'T00:00:00');
                return { year: firstDate.getFullYear(), month: firstDate.getMonth() };
            }
            const firstDate = new Date(availability[0].date + 'T00:00:00');
            return { year: firstDate.getFullYear(), month: firstDate.getMonth() };
        }
        const today = new Date();
        return { year: today.getFullYear(), month: today.getMonth() };
    }, [availability]);

    const [viewMonth, setViewMonth] = useState(initialMonth);

    useEffect(() => {
        const loadBooking = async () => {
            if (!token) {
                setError('Invalid manage link');
                setIsLoading(false);
                return;
            }

            try {
                const data = await manageBookingApi.get(token, timezone);
                setBooking(data.booking);
                setLink(data.link);
                setPolicy(data.policy);
                setAvailability(data.availability || []);
                if (data.timezone) {
                    setTimezone(data.timezone);
                }
            } catch (err: any) {
                setError(err.message || 'Failed to load booking');
            } finally {
                setIsLoading(false);
            }
        };

        loadBooking();
    }, [token, timezone]);

    const getDaysInMonth = (year: number, month: number) => {
        return new Date(year, month + 1, 0).getDate();
    };

    const getFirstDayOfMonth = (year: number, month: number) => {
        return new Date(year, month, 1).getDay();
    };

    const availableDates = useMemo(() => {
        return new Set(availability.filter(d => d.slots.length > 0).map(d => d.date));
    }, [availability]);

    const getSlotsForDate = (date: string): AvailabilitySlot[] => {
        const day = availability.find(d => d.date === date);
        return day?.slots || [];
    };

    const navigateMonth = (direction: 'prev' | 'next') => {
        setViewMonth(prev => {
            const newMonth = direction === 'next' ? prev.month + 1 : prev.month - 1;
            if (newMonth > 11) {
                return { year: prev.year + 1, month: 0 };
            } else if (newMonth < 0) {
                return { year: prev.year - 1, month: 11 };
            }
            return { year: prev.year, month: newMonth };
        });
    };

    const handleReschedule = async () => {
        if (!selectedSlot || !token) return;

        setIsSubmitting(true);
        try {
            const result = await manageBookingApi.reschedule(token, {
                new_slot: {
                    start_time_utc: selectedSlot.start_time_utc,
                    end_time_utc: selectedSlot.end_time_utc
                },
                timezone
            });
            setSuccessMessage(result.message || 'Your meeting has been rescheduled!');
            setView('success');
        } catch (err: any) {
            setError(err.message || 'Failed to reschedule');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = async () => {
        if (!token) return;

        setIsSubmitting(true);
        try {
            const result = await manageBookingApi.cancel(token, { reason: cancelReason || undefined });
            setSuccessMessage(result.message || 'Your booking has been cancelled.');
            setView('success');
        } catch (err: any) {
            setError(err.message || 'Failed to cancel booking');
        } finally {
            setIsSubmitting(false);
            setShowCancelConfirm(false);
        }
    };

    const formatNoticeHours = (hours: number): string => {
        if (hours < 1) return 'no notice';
        if (hours === 1) return '1 hour';
        if (hours < 24) return `${hours} hours`;
        if (hours === 24) return '24 hours (1 day)';
        if (hours === 48) return '48 hours (2 days)';
        const days = Math.floor(hours / 24);
        return `${hours} hours (${days} days)`;
    };

    if (isLoading) {
        return (
            <div className="booking-page aam-full-page">
                <div className="aam-page-content">
                    <div style={{ textAlign: 'center' }}>
                        <div className="spinner" style={{ margin: '0 auto 1rem' }} />
                        <p>Loading booking details...</p>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    if (error && !booking) {
        return (
            <div className="booking-page aam-full-page">
                <div className="aam-header">
                    <div className="aam-header-content">
                        <div className="aam-header-logo">
                            <img src={isDark ? "/logo.png" : "/light_logo.png"} alt="AAM Let's Meet" className="navbar-logo" />
                        </div>
                        <button
                            onClick={toggleTheme}
                            className="theme-toggle"
                            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                        >
                            {isDark ? <Sun size={20} /> : <Moon size={20} />}
                        </button>
                    </div>
                </div>
                <div className="aam-page-content">
                    <div className="aam-error-state">
                        <div className="aam-error-icon">
                            <AlertTriangle size={48} />
                        </div>
                        <h2>Booking Not Found</h2>
                        <p>{error}</p>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    if (!booking || !link || !policy) return null;

    if (view === 'success') {
        return (
            <div className="booking-page aam-full-page">
                <div className="aam-header">
                    <div className="aam-header-content">
                        <div className="aam-header-logo">
                            <img src={isDark ? "/logo.png" : "/light_logo.png"} alt="AAM Let's Meet" className="navbar-logo" />
                        </div>
                        <button
                            onClick={toggleTheme}
                            className="theme-toggle"
                            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                        >
                            {isDark ? <Sun size={20} /> : <Moon size={20} />}
                        </button>
                    </div>
                </div>
                <div className="aam-page-content">
                    <div className="aam-success">
                        <div className="aam-success-icon">
                            <Check size={48} />
                        </div>
                        <h2>{successMessage}</h2>
                        <p>You will receive a confirmation email shortly.</p>
                        <button
                            onClick={() => window.close()}
                            className="aam-submit-btn"
                            style={{ marginTop: 'var(--space-4)' }}
                        >
                            Close This Page
                        </button>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    if (booking.cancelled_at) {
        return (
            <div className="booking-page aam-full-page">
                <div className="aam-header">
                    <div className="aam-header-content">
                        <div className="aam-header-logo">
                            <img src={isDark ? "/logo.png" : "/light_logo.png"} alt="AAM Let's Meet" className="navbar-logo" />
                        </div>
                        <button
                            onClick={toggleTheme}
                            className="theme-toggle"
                            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                        >
                            {isDark ? <Sun size={20} /> : <Moon size={20} />}
                        </button>
                    </div>
                </div>
                <div className="aam-page-content">
                    <div className="aam-error-state">
                        <div className="aam-error-icon">
                            <X size={48} />
                        </div>
                        <h2>Booking Cancelled</h2>
                        <p>
                            This booking for <strong>{link.name}</strong> has been cancelled.
                        </p>
                        {booking.cancel_reason && (
                            <p style={{ fontSize: 'var(--font-size-sm)', marginTop: 'var(--space-2)' }}>
                                Reason: {booking.cancel_reason}
                            </p>
                        )}
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="booking-page aam-full-page">
            <div className="aam-header">
                <div className="aam-header-content">
                    <div className="aam-header-logo">
                        <img src={isDark ? "/logo.png" : "/light_logo.png"} alt="AAM Let's Meet" className="navbar-logo" />
                    </div>
                    <button
                        onClick={toggleTheme}
                        className="theme-toggle"
                        title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                        aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                    >
                        {isDark ? <Sun size={20} /> : <Moon size={20} />}
                    </button>
                </div>
            </div>

            <div className="aam-page-content">
                <div style={{ maxWidth: '900px', width: '100%' }}>
                    {error && (
                        <div className="alert alert-error" style={{ marginBottom: 'var(--space-4)' }}>
                            {error}
                            <button onClick={() => setError(null)} style={{ marginLeft: 'var(--space-2)', textDecoration: 'underline' }}>Dismiss</button>
                        </div>
                    )}

                    {view === 'details' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                            <div className="card">
                                <h2 className="card-title" style={{ marginBottom: 'var(--space-4)' }}>
                                    Manage Your Booking
                                </h2>
                                <p style={{ marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-sm)', opacity: 0.8 }}>
                                    {link.name} with {link.user_name}
                                </p>
                                
                                <h3 className="card-title" style={{ marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-lg)' }}>
                                    Booking Details
                                </h3>
                                
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)' }}>
                                        <Calendar size={20} style={{ color: 'var(--color-primary)', marginTop: '2px', flexShrink: 0 }} />
                                        <div>
                                            <p style={{ fontWeight: 500, marginBottom: 'var(--space-1)' }}>
                                                {booking.meeting_time_formatted}
                                            </p>
                                            <p style={{ fontSize: 'var(--font-size-sm)', opacity: 0.8 }}>
                                                {link.duration_minutes} minutes
                                            </p>
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)' }}>
                                        <User size={20} style={{ color: 'var(--color-primary)', marginTop: '2px', flexShrink: 0 }} />
                                        <div>
                                            <p style={{ fontWeight: 500, marginBottom: 'var(--space-1)' }}>
                                                {link.user_name}
                                            </p>
                                            <p style={{ fontSize: 'var(--font-size-sm)', opacity: 0.8 }}>
                                                Host
                                            </p>
                                        </div>
                                    </div>

                                    {booking.teams_meeting_url && (
                                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)' }}>
                                            <ExternalLink size={20} style={{ color: 'var(--color-primary)', marginTop: '2px', flexShrink: 0 }} />
                                            <div>
                                                <a 
                                                    href={booking.teams_meeting_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{ color: 'var(--color-primary)', fontWeight: 500 }}
                                                >
                                                    Join Teams Meeting
                                                </a>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {policy.min_notice_hours > 0 && (
                                <div className="alert alert-warning">
                                    <AlertTriangle size={16} style={{ marginRight: 'var(--space-2)', flexShrink: 0 }} />
                                    <span style={{ fontSize: 'var(--font-size-sm)' }}>
                                        Changes must be made at least {formatNoticeHours(policy.min_notice_hours)} before the meeting.
                                    </span>
                                </div>
                            )}

                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                                {policy.allow_reschedule && (
                                    <button
                                        onClick={() => setView('reschedule')}
                                        disabled={!policy.can_reschedule.allowed}
                                        className={policy.can_reschedule.allowed ? 'btn btn-primary' : 'btn btn-primary'}
                                        style={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center', 
                                            gap: 'var(--space-2)',
                                            opacity: policy.can_reschedule.allowed ? 1 : 0.5,
                                            cursor: policy.can_reschedule.allowed ? 'pointer' : 'not-allowed'
                                        }}
                                        title={policy.can_reschedule.reason || undefined}
                                    >
                                        <RefreshCw size={20} />
                                        Reschedule
                                    </button>
                                )}
                                
                                {policy.allow_cancel && (
                                    <button
                                        onClick={() => setView('cancel')}
                                        disabled={!policy.can_cancel.allowed}
                                        className={policy.can_cancel.allowed ? 'btn btn-secondary' : 'btn btn-secondary'}
                                        style={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center', 
                                            gap: 'var(--space-2)',
                                            opacity: policy.can_cancel.allowed ? 1 : 0.5,
                                            cursor: policy.can_cancel.allowed ? 'pointer' : 'not-allowed'
                                        }}
                                        title={policy.can_cancel.reason || undefined}
                                    >
                                        <X size={20} />
                                        Cancel Booking
                                    </button>
                                )}
                            </div>

                            {!policy.can_reschedule.allowed && policy.can_reschedule.reason && (
                                <p style={{ fontSize: 'var(--font-size-sm)', opacity: 0.7 }}>
                                    {policy.can_reschedule.reason}
                                </p>
                            )}
                            {!policy.can_cancel.allowed && policy.can_cancel.reason && !policy.can_reschedule.reason && (
                                <p style={{ fontSize: 'var(--font-size-sm)', opacity: 0.7 }}>
                                    {policy.can_cancel.reason}
                                </p>
                            )}
                        </div>
                    )}

                    {view === 'reschedule' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                            <button
                                onClick={() => { setView('details'); setSelectedDate(null); setSelectedSlot(null); }}
                                className="aam-back-btn"
                            >
                                <ChevronLeft size={16} />
                                Back to booking details
                            </button>

                            <div className="card">
                                <h2 className="card-title" style={{ marginBottom: 'var(--space-4)' }}>
                                    Select a New Time
                                </h2>

                                <div className={`aam-calendar-section ${selectedDate ? 'has-times' : ''}`} style={{ maxWidth: 'none', padding: 0 }}>
                                    <div style={{ marginBottom: 'var(--space-4)' }}>
                                        <label className="aam-label" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                                            <Globe size={16} />
                                            Timezone
                                        </label>
                                        <div className="aam-timezone-select">
                                            <Globe size={14} />
                                            <select
                                                value={timezone}
                                                onChange={(e) => setTimezone(e.target.value)}
                                                className="aam-input"
                                            >
                                                {TIMEZONES.map(tz => (
                                                    <option key={tz.value} value={tz.value}>
                                                        {tz.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="aam-calendar-header">
                                        <button
                                            onClick={() => navigateMonth('prev')}
                                            className="aam-nav-btn"
                                            aria-label="Previous month"
                                        >
                                            <ChevronLeft size={20} />
                                        </button>
                                        <span className="aam-month-title">
                                            {MONTH_NAMES[viewMonth.month]} {viewMonth.year}
                                        </span>
                                        <button
                                            onClick={() => navigateMonth('next')}
                                            className="aam-nav-btn"
                                            aria-label="Next month"
                                        >
                                            <ChevronRight size={20} />
                                        </button>
                                    </div>

                                    <div className="aam-weekdays">
                                        {DAYS_OF_WEEK.map((day, i) => (
                                            <div key={i} className="aam-weekday">{day}</div>
                                        ))}
                                    </div>

                                    <div className="aam-calendar-grid">
                                        {Array.from({ length: getFirstDayOfMonth(viewMonth.year, viewMonth.month) }).map((_, i) => (
                                            <div key={`empty-${i}`} />
                                        ))}
                                        {Array.from({ length: getDaysInMonth(viewMonth.year, viewMonth.month) }).map((_, i) => {
                                            const day = i + 1;
                                            const dateStr = `${viewMonth.year}-${String(viewMonth.month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                                            const hasSlots = availableDates.has(dateStr);
                                            const isSelected = selectedDate === dateStr;
                                            const today = new Date();
                                            const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
                                            const isToday = dateStr === todayStr;
                                            const isPast = dateStr < todayStr;

                                            return (
                                                <button
                                                    key={day}
                                                    onClick={() => hasSlots && !isPast && setSelectedDate(dateStr)}
                                                    disabled={!hasSlots || isPast}
                                                    className={`aam-day current-month ${hasSlots && !isPast ? 'available' : ''} ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''}`}
                                                >
                                                    <span className="aam-day-number">{day}</span>
                                                    {isToday && <span className="aam-today-dot" />}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                {selectedDate && (
                                    <div style={{ marginTop: 'var(--space-4)' }}>
                                        <h3 className="aam-times-title" style={{ marginBottom: 'var(--space-3)' }}>
                                            Available times for {formatDateReadable(selectedDate)}
                                        </h3>
                                        <div className="aam-times-list">
                                            {getSlotsForDate(selectedDate).map((slot, idx) => (
                                                <div key={idx} className="aam-time-row">
                                                    <button
                                                        onClick={() => setSelectedSlot(slot)}
                                                        className={`aam-time-btn ${selectedSlot?.start_time_utc === slot.start_time_utc ? 'selected' : ''}`}
                                                    >
                                                        {slot.display}
                                                        {selectedSlot?.start_time_utc === slot.start_time_utc && (
                                                            <Check size={16} className="aam-time-check" />
                                                        )}
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {selectedSlot && (
                                <div className="card">
                                    <h3 className="card-title" style={{ marginBottom: 'var(--space-4)' }}>
                                        Confirm New Time
                                    </h3>
                                    <p style={{ marginBottom: 'var(--space-4)' }}>
                                        <strong>{formatDateReadable(selectedDate!)}</strong> at {selectedSlot.display}
                                    </p>
                                    <button
                                        onClick={handleReschedule}
                                        disabled={isSubmitting}
                                        className="aam-submit-btn"
                                    >
                                        {isSubmitting ? 'Rescheduling...' : 'Confirm Reschedule'}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {view === 'cancel' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                            <button
                                onClick={() => { setView('details'); setCancelReason(''); setShowCancelConfirm(false); }}
                                className="aam-back-btn"
                            >
                                <ChevronLeft size={16} />
                                Back to booking details
                            </button>

                            <div className="card">
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '48px', height: '48px', borderRadius: '50%', background: 'rgba(220, 38, 38, 0.15)', marginBottom: 'var(--space-4)' }}>
                                    <AlertTriangle size={24} style={{ color: 'var(--color-error)' }} />
                                </div>
                                <h2 className="card-title" style={{ marginBottom: 'var(--space-2)' }}>
                                    Cancel Your Booking?
                                </h2>
                                <p style={{ marginBottom: 'var(--space-4)', opacity: 0.8 }}>
                                    Are you sure you want to cancel your {link.name} meeting with {link.user_name}?
                                </p>

                                <div className="aam-form-group" style={{ marginBottom: 'var(--space-4)' }}>
                                    <label className="aam-label">
                                        Reason for cancellation (optional)
                                    </label>
                                    <div className="aam-input-wrapper textarea">
                                        <MessageSquare size={18} />
                                        <textarea
                                            value={cancelReason}
                                            onChange={(e) => setCancelReason(e.target.value)}
                                            placeholder="Let the host know why you're cancelling..."
                                            rows={3}
                                            className="aam-textarea"
                                        />
                                    </div>
                                </div>

                                {!showCancelConfirm ? (
                                    <button
                                        onClick={() => setShowCancelConfirm(true)}
                                        className="btn btn-danger"
                                        style={{ width: '100%' }}
                                    >
                                        Cancel Booking
                                    </button>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                                        <p style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500, color: 'var(--color-error)' }}>
                                            This action cannot be undone.
                                        </p>
                                        <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
                                            <button
                                                onClick={() => setShowCancelConfirm(false)}
                                                className="btn btn-secondary"
                                                style={{ flex: 1 }}
                                            >
                                                Go Back
                                            </button>
                                            <button
                                                onClick={handleCancel}
                                                disabled={isSubmitting}
                                                className="btn btn-danger"
                                                style={{ flex: 1 }}
                                            >
                                                {isSubmitting ? 'Cancelling...' : 'Confirm Cancel'}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <Footer />
        </div>
    );
}
